/**
 * Fiches du Codex – cours et référence pour l'analyse harmonique.
 * Chaque fiche : id, title, shortDescription, content, relatedNodeIds, relatedTags, category, order.
 */

export const CODEX_CATEGORIES = {
  fonctions: 'Fonctions',
  cadences: 'Cadences',
  enchainements: 'Enchaînements',
  renversements: 'Renversements',
  couleurs: 'Couleurs',
  parcours: 'Parcours',
  contexte: 'Contexte et repères'
}

/** Ordre d'affichage des catégories dans la liste */
export const CODEX_CATEGORY_ORDER = ['fonctions', 'cadences', 'enchainements', 'renversements', 'couleurs', 'parcours', 'contexte']

export const CODEX_ENTRIES = [
  // --- Fonctions ---
  {
    id: 'fiche-fonctions-tsd-d',
    title: 'Les fonctions T, SD et D',
    shortDescription: 'Tonique, Sous-Dominante et Dominante : le socle de l\'analyse harmonique.',
    content: `En analyse fonctionnelle (Riemann), chaque accord est lu selon sa **fonction** dans la phrase. Voir les sections ci-dessous.
- **T (Tonique)** : stabilité, point d’arrivée. Degrés principaux : I ; parallèles : III, VI.
- **SD (Sous-Dominante)** : prépare la dominante. Degrés principaux : IV ; parallèles : II, VI.
- **D (Dominante)** : tension, attente de résolution. Degrés principaux : V ; parallèles : VII, III.

La chaîne **T → SD → D → T** résume le parcours harmonique de la plupart des phrases tonales. En exercice, on vous demande d’identifier la fonction (T, SD ou D) de chaque accord.`,
    relatedNodeIds: ['1.1', '1.2'],
    relatedTags: ['Tonique', 'Dominante', 'SousDominante'],
    category: 'fonctions',
    order: 1,
    sections: [
      { id: 'definition', title: 'Définition', content: `- **T (Tonique)** : stabilité, point d'arrivée. Degrés principaux : I ; parallèles : III, VI.\n- **SD (Sous-Dominante)** : prépare la dominante. Degrés principaux : IV ; parallèles : II, VI.\n- **D (Dominante)** : tension, attente de résolution. Degrés principaux : V ; parallèles : VII, III.` },
      { id: 'parcours', title: 'Parcours harmonique', content: `La chaîne **T → SD → D → T** résume le parcours harmonique de la plupart des phrases tonales.` },
      { id: 'exercice', title: 'En exercice', content: `On vous demande d'identifier la fonction (T, SD ou D) de chaque accord.` },
      { id: 'mineur', title: 'En mineur', content: `Les fonctions restent les mêmes, mais certains accords changent de nature : ii est diminué, III et VI sont majeurs, et la sensible doit être altérée pour créer la dominante.` }
    ]
  },
  {
    id: 'fiche-tonique',
    title: 'La fonction Tonique (T)',
    shortDescription: 'Stabilité et point d’ancrage : I, I6, et parallèles.',
    content: `La **Tonique** donne la stabilité. Elle ouvre et conclut les phrases.
- **Degré principal** : I (accord de tonique à l’état fondamental).
- **Renversement** : I6 garde la fonction tonique ; c’est la basse qui change.
- **Parallèles** : III et VI peuvent avoir une couleur de tonique selon le contexte.

En exercice : si l’accord « sonne » comme un point d’arrivée ou de départ, pensez T.

*En mineur : la tonique est un accord mineur (i). Les parallèles III (majeur) et VI (majeur) gardent une couleur de tonique.*`,
    relatedNodeIds: ['1.1', '2.1'],
    relatedTags: ['Tonique'],
    category: 'fonctions',
    order: 2
  },
  {
    id: 'fiche-sous-dominante',
    title: 'La fonction Sous-Dominante (SD)',
    shortDescription: 'IV, II et parallèles : préparer la dominante.',
    content: `La **Sous-Dominante** prépare la dominante. Elle renforce la direction vers V.
- **Degrés principaux** : IV (le plus courant), II (et II6) comme substituts.
- En exercice : entre T et D, un accord qui « mène » vers V est souvent SD.

II et II6 ont la même fonction que IV, avec une couleur différente (plus mineure pour ii).

*En mineur : le II est naturellement **diminué** (quinte diminuée). Le IV est mineur (iv).*`,
    relatedNodeIds: ['1.2', '3.1'],
    relatedTags: ['SousDominante'],
    category: 'fonctions',
    order: 3
  },
  {
    id: 'fiche-dominante',
    title: 'La fonction Dominante (D)',
    shortDescription: 'V, V7 et renversements : la tension qui attire la tonique.',
    content: `La **Dominante** crée la tension. Elle attire la tonique (I).
- **Degrés** : V (triade), V7 (septième de dominante), et renversements V6, V6/5, V4/3, V2.
- **VII°** (accord de sensible) : dominante sans fondamentale, même tension que V7.

En phrase : tout accord qui « demande » une résolution vers I est typiquement D. La cadence parfaite V→I est la conclusion type.

🎯 **Mouvement obligé** : la sensible (7e degré) doit **monter** vers la tonique. Dans V7, la septième doit **descendre**.
*En mineur : la sensible est une note **altérée** (accidentelle) — ne jamais l'oublier dans V et V7.*`,
    relatedNodeIds: ['1.1', '2.3', '3.2'],
    relatedTags: ['Dominante', 'Septième'],
    category: 'fonctions',
    order: 4
  },
  // --- Cadences ---
  {
    id: 'fiche-demi-cadence-parfaite',
    title: 'Demi-cadence et cadence parfaite',
    shortDescription: 'Fin sur V (suspens) vs V→I (conclusion).',
    content: `**Demi-cadence** : la phrase se termine sur V. La phrase reste en suspens (on attend la suite).
**Cadence parfaite** : V → I. C’est la conclusion type ; la phrase est close.

En exercice, on vous demande parfois d’identifier la **cadence** en plus de la fonction de chaque accord. Une demi-cadence = fin sur D (V) ; une cadence parfaite = enchaînement V puis I.`,
    relatedNodeIds: ['1.1', 'cadence-demi', 'cadence-parfaite', 'cadence-demi-et-parfaite'],
    relatedTags: ['DemiCadence', 'CadenceParfaite'],
    category: 'cadences',
    order: 1,
    sections: [
      { id: 'demi-cadence', title: 'Demi-cadence', content: `La phrase se termine sur **V**. La phrase reste en suspens (on attend la suite).` },
      { id: 'cadence-parfaite', title: 'Cadence parfaite', content: `**V → I**. C'est la conclusion type ; la phrase est close.` },
      { id: 'exercice', title: 'En exercice', content: `On vous demande parfois d'identifier la **cadence** en plus de la fonction de chaque accord. Une demi-cadence = fin sur D (V) ; une cadence parfaite = enchaînement V puis I.` },
      { id: 'analogie', title: 'Analogie linguistique', content: `La demi-cadence est la **virgule** (,) ou la **question** (?) du discours musical : pause, on attend la suite. La cadence parfaite est le **point** (.) : conclusion définitive.` },
      { id: 'mineur', title: 'En mineur', content: `La sensible doit être **altérée** (accidentelle) dans l'accord de V. C'est ce qui crée l'attraction vers la tonique.` }
    ]
  },
  {
    id: 'fiche-cadence-plagale',
    title: 'Cadence plagale (IV→I)',
    shortDescription: 'Conclusion douce IV → I.',
    content: `La **cadence plagale** est l’enchaînement **IV → I**. Elle apporte une conclusion douce, souvent utilisée après la cadence parfaite (V→I) dans les finales.

En exercice : si vous voyez une fin IV puis I (sans V entre les deux), c’est une cadence plagale. La fonction de IV est SD, celle de I est T.

💡 *Analogie : la cadence plagale est le **post-scriptum** (P.S.) ou l'« **Amen** » du discours musical.*

**La coda plagale** : quand IV → I est placé **après** une cadence parfaite, on parle de coda plagale (extension post-cadentielle). Variantes : IV → I (classique), iv → I en majeur (emprunt au mineur, couleur romantique).`,
    relatedNodeIds: ['1.2', 'cadence-plagale'],
    relatedTags: ['CadencePlagale'],
    category: 'cadences',
    order: 2
  },
  {
    id: 'fiche-cadence-64',
    title: 'Le six-quatre de cadence',
    shortDescription: 'I6/4 avant V : il appartient à la dominante.',
    content: `Le **six-quatre de cadence** est la formule **I6/4 → V → I**. Malgré le chiffrage en I, ce I6/4 est une **appoggiature** : il appartient à la **dominante**, pas à la tonique. En analyse, on le considère comme D (tension vers V).

En exercice : devant un V, un accord 6/4 sur la tonique est en général à classer **D** (cadence 6/4), pas T.

🎯 **Mouvement obligé** : la basse reste sur le **degré V** du 6/4 cadentiel jusqu'à la résolution.

**Les trois types de 6/4 :**
- **6/4 cadentiel** : I6/4 → V → I (appoggiature de la dominante, temps fort).
- **6/4 de passage** : relie deux positions par mouvement conjoint de la basse (ex. I → V6/4 → I6).
- **6/4 de broderie** : la basse fait une pédale, les voix supérieures brodent au-dessus (temps faible).`,
    relatedNodeIds: ['2.2', 'cadence-parfaite-composee', 'broderie-64'],
    relatedTags: ['Cadence6/4'],
    category: 'cadences',
    order: 3
  },
  {
    id: 'fiche-cadence-rompue',
    title: 'Cadence rompue (V→vi)',
    shortDescription: 'V→vi : la fausse résolution.',
    content: `La **cadence rompue** (ou « évitée ») est l’enchaînement **V → vi** au lieu de V → I. La dominante ne résout pas sur la tonique mais sur le **VI** (tonique parallèle), ce qui crée un effet de suspens ou de surprise.

En exercice : après un V, si la résolution est sur vi (et non I), il s’agit d’une cadence rompue.

💡 *Analogie : la cadence rompue est le « Mais... » du discours musical — on attendait la fin, le récit rebondit.*
🎯 **Mouvement obligé** : doubler la **tierce** du VI (et non la fondamentale) pour éviter les quintes et octaves parallèles.
*En mineur : le VI est naturellement **majeur** (♭VI).*`,
    relatedNodeIds: ['3.3', 'cadence-rompue'],
    relatedTags: ['CadenceRompue'],
    category: 'cadences',
    order: 4
  },
  // --- Enchaînements ---
  {
    id: 'fiche-enchainements-typiques',
    title: 'Enchaînements typiques (T–SD–D–T)',
    shortDescription: 'Le parcours harmonique de la phrase tonale.',
    content: `La plupart des phrases tonales suivent un parcours du type :
**T → (SD) → D → T**
- T donne la stabilité au départ.
- SD prépare D (optionnel mais très fréquent).
- D crée la tension.
- T conclut (résolution).

En exercice : repérez d’abord les cadences (demi-cadence = fin sur D ; cadence parfaite = D puis T), puis remplissez les fonctions des accords entre les deux.

*En mineur : le parcours est identique (T → SD → D → T), mais la sensible doit être altérée et certains accords changent de qualité (ii°, III+, etc.).*`,
    relatedNodeIds: ['1.1', '1.2', 'revision-etage-1'],
    relatedTags: ['ProgressionII-V-I'],
    category: 'enchainements',
    order: 1
  },
  // --- Renversements ---
  {
    id: 'fiche-renversements',
    title: 'Renversements',
    shortDescription: 'Même fonction, autre position de la basse.',
    content: `Un **renversement** change la note à la basse, pas la fonction. Par exemple :
- **I6** : tonique avec tierce à la basse (même fonction T que I).
- **V6**, **V6/5**, **V4/3**, **V2** : tous restent **D** (dominante).

En exercice : dès que la basse n’est plus la fondamentale de l’accord, vous devez identifier le renversement (6, 6/4, 7, 6/5, 4/3, 2) tout en gardant la bonne fonction.

*En mineur : les renversements fonctionnent de la même manière. Attention cependant au VII° (diminué) dont les renversements ont une couleur très différente de ceux du V.*`,
    relatedNodeIds: ['2.1', '2.2', '2.3', '3.2'],
    relatedTags: ['PremierRenversement', 'SecondRenversement', 'SeptièmePremierRenversement', 'SeptièmeSecondRenversement', 'SeptièmeTroisièmeRenversement'],
    category: 'renversements',
    order: 1
  },
  // --- Couleurs (résumé pour étage 4) ---
  {
    id: 'fiche-sixte-napolitaine',
    title: 'Sixte napolitaine (N6)',
    shortDescription: 'II♭6 dans la fonction SD.',
    content: `La **sixte napolitaine** est un accord de **sous-dominante** : **II♭ à l’état de premier renversement** (N6 ou II♭6). Il ne faut pas le confondre avec IV ou ii6. Il apporte une couleur sombre, souvent avant V.

En exercice : dans le menu SD, choisir N6 (ou II♭6) quand l’accord a cette couleur caractéristique.`,
    relatedNodeIds: ['4.1'],
    relatedTags: ['Napolitaine'],
    category: 'couleurs',
    order: 1
  },
  {
    id: 'fiche-dominante-dominante',
    title: 'Dominante de la dominante (V/V)',
    shortDescription: 'V7 de V : renforcer la tension vers V.',
    content: `La **dominante de la dominante** (V/V ou V7/V) est un accord qui « prépare » V en le traitant comme une tonique temporaire. C’est une couleur chromatique très courante.

En exercice : un accord de septième qui résout sur V (et non sur I) peut être V7/V. Les renversements (V6/5/V, etc.) gardent cette fonction.

*En mineur : le V/V introduit un double chromatisme (la sensible du ton + le #4 qui devient sensible temporaire de V).*`,
    relatedNodeIds: ['4.2', '4.3', '4.4'],
    relatedTags: [],
    category: 'couleurs',
    order: 2
  },
  // --- Nouvelles fiches (synthèse, ponctuation, imparfaite, tierce picarde, mouvements obligés) ---
  {
    id: 'fiche-anatomie-phrase',
    title: 'Anatomie d\'une phrase harmonique',
    shortDescription: 'Un exemple de parcours cadentiel complet en 6 étapes.',
    content: `La plupart des phrases tonales suivent un **parcours type** que l'on peut décomposer en 6 étapes, de la stabilité initiale à la résolution finale. Ce schéma n'est pas une règle absolue, mais un modèle fréquent chez Bach, Mozart et Chopin.

**[ T ] → [ SD ] → [ (D) ] → [ Pivot 6/4 ] → [ D ] → [ T ]**

Ce chemin se lit de gauche à droite : chaque étape prépare la suivante. Voir les sections ci-dessous pour le détail de chaque étape.`,
    relatedNodeIds: ['cadence-parfaite-composee', 'revision-etage-2'],
    relatedTags: ['ProgressionII-V-I', 'CadenceParfaite'],
    category: 'enchainements',
    order: 2,
    sections: [
      { id: 'etape-1', title: '1. T — Tonique (départ)', content: `Point de départ stable. Accords : **I** (fondamental), **I6** (1er renversement).\n*En mineur : i, i6.*` },
      { id: 'etape-2', title: '2. SD — Sous-Dominante (préparation)', content: `Prépare la tension. Accords : **IV**, **II**, **II6**, **II6/5** (avec 7e). La **Sixte Napolitaine (N6)** se place ici, surtout en mineur.\n*En mineur : iv, ii° (quinte diminuée).*` },
      { id: 'etape-3', title: '3. (D) — Dominante secondaire (optionnel)', content: `Pour dramatiser l'arrivée sur V, on transforme un accord SD en « dominante de la dominante » (V/V ou V7/V). Exemple en Do : Ré Majeur (ou Ré7) cible Sol.\n*Étape optionnelle, fréquente dans le style classique et romantique.*` },
      { id: 'etape-4', title: '4. Pivot — Cadentiel 6/4 (optionnel)', content: `**I6/4** : double appoggiature de la dominante. La basse est sur le degré V et y reste pour l'étape suivante. Ce n'est pas une vraie tonique.\n*Crée un effet de suspension avant la résolution.*` },
      { id: 'etape-5', title: '5. D — Dominante (tension)', content: `Tension maximale. Accords : **V**, **V7**, renversements (**V6/5**, **V4/3**, **V2**). **VII°** fonctionne aussi comme dominante.\n*En mineur : ne jamais oublier l'altération accidentelle sur la sensible.*\n🛑 Si la phrase s'arrête ici sans résoudre → **demi-cadence** (la virgule musicale).` },
      { id: 'etape-6', title: '6. T — Résolution (arrivée)', content: `Retour à la stabilité. Selon le type de résolution :\n- **Cadence parfaite** : V → I fondamentaux (le point final).\n- **Cadence imparfaite** : V → I6 ou V6 → I (le point-virgule).\n- **Cadence rompue** : V → VI (surprise, le « mais... »).\n*Après la résolution, une **coda plagale** (IV → I) peut prolonger la fin.*` }
    ]
  },
  {
    id: 'fiche-ponctuation-musicale',
    title: 'La ponctuation musicale',
    shortDescription: 'Cadences et ponctuation : la musique est un discours.',
    content: `L'harmonie tonale fonctionne comme un **discours**. Les cadences sont la **ponctuation** de la phrase musicale : elles créent le rythme du récit, marquent les pauses et les conclusions.`,
    relatedNodeIds: ['cadence-demi-et-parfaite', 'cadence-plagale', 'cadence-rompue', 'cadence-imparfaite'],
    relatedTags: ['CadenceParfaite', 'DemiCadence', 'CadencePlagale', 'CadenceRompue', 'CadenceImparfaite'],
    category: 'cadences',
    order: 0,
    sections: [
      { id: 'point', title: 'Cadence parfaite = Point (.)', content: `**V → I** tous deux en position fondamentale. La phrase est **terminée**, conclusive. Rien à ajouter.` },
      { id: 'virgule', title: 'Demi-cadence = Virgule (,) ou Question (?)', content: `Fin sur **V**. La phrase est en **suspens** : on attend la suite. Comme une question qui appelle une réponse.` },
      { id: 'point-virgule', title: 'Cadence imparfaite = Point-virgule (;)', content: `V → I avec inversion (ex. V → I6, ou V6 → I). La conclusion est **partielle** : le propos n'est pas entièrement clos.` },
      { id: 'mais', title: 'Cadence rompue = « Mais... » (!)', content: `**V → VI** au lieu de V → I. On attendait la fin, le discours **rebondit**. Effet de surprise.` },
      { id: 'ps', title: 'Cadence plagale = Post-scriptum (P.S.)', content: `**IV → I** après la cadence parfaite. Un **ajout doux** après le point final. L'« Amen » musical.` },
      { id: 'emphase', title: 'Cadence parfaite composée = Point emphatique', content: `**I6/4 → V → I**. On **retient son souffle** (6/4) avant de conclure avec force.` }
    ]
  },
  {
    id: 'fiche-cadence-imparfaite',
    title: 'Cadence imparfaite',
    shortDescription: 'V→I avec inversion : conclusion partielle.',
    content: `La **cadence imparfaite** est une variante de la cadence parfaite où l'un des deux accords (V ou I) est en **renversement**, ou bien la mélodie ne termine pas sur la tonique. La conclusion est présente mais **incomplète**.

En exercice : si V résout sur I mais que l'un des deux est renversé (ex. V → I6, V6 → I), c'est une cadence imparfaite.

💡 *Analogie : c'est le **point-virgule** (;) du discours musical — la phrase avance mais n'est pas tout à fait close.*`,
    relatedNodeIds: ['cadence-imparfaite', '2.1'],
    relatedTags: ['CadenceImparfaite'],
    category: 'cadences',
    order: 5
  },
  {
    id: 'fiche-tierce-picarde',
    title: 'La Tierce Picarde',
    shortDescription: 'Résolution sur I majeur à la fin d\'un morceau en mineur.',
    content: `La **Tierce Picarde** est l'usage d'un accord de tonique **majeur** (I) à la fin d'une pièce écrite en mode **mineur**. La tierce de l'accord final est haussée, transformant le i mineur en I majeur.

Effet : une **lumière inattendue** après l'ombre du mineur. Très fréquente chez Bach et dans la musique baroque.

En exercice : dans une tonalité mineure, si le dernier accord est un I majeur, c'est une Tierce Picarde.`,
    relatedNodeIds: ['4.1'],
    relatedTags: ['TiercePicarde'],
    category: 'couleurs',
    order: 3
  },
  {
    id: 'fiche-mouvements-obliges',
    title: 'Les mouvements obligés',
    shortDescription: 'Sensible qui monte, septième qui descend : les règles de conduite.',
    content: `Certains mouvements mélodiques sont **obligatoires** dans l'harmonie tonale. Les respecter est la clé d'un enchaînement correct et d'une écriture propre.`,
    relatedNodeIds: ['cadence-parfaite-composee', 'revision-etage-2', 'cadence-rompue'],
    relatedTags: ['CadenceParfaite', 'CadenceRompue', 'Dominante'],
    category: 'enchainements',
    order: 3,
    sections: [
      { id: 'sensible', title: 'La sensible monte', content: `Le **7e degré** (sensible) doit **monter** d'un demi-ton vers la tonique. C'est l'attraction fondamentale de la dominante.\n*En mineur, la sensible est une note altérée (accidentelle) : ne pas l'oublier.*` },
      { id: 'septieme', title: 'La septième descend', content: `Dans un accord de 7e (V7, II7...), la **septième** doit **descendre** par mouvement conjoint. C'est un mouvement obligé qui donne sa couleur à la résolution.` },
      { id: 'rompue', title: 'Cadence rompue : doubler la tierce', content: `Quand V résout sur VI (cadence rompue), on **double la tierce** du VI pour éviter les quintes et octaves parallèles. C'est une exception à la règle habituelle de doubler la fondamentale.` },
      { id: 'cadentiel', title: 'Le 6/4 cadentiel : la basse reste', content: `Dans la formule I6/4 → V → I, la basse est sur le **degré V** et doit y **rester** pour la résolution. Le 6/4 est une appoggiature, pas un changement de basse.` }
    ]
  },
  // --- Contexte et repères (notes historiques, compositeurs, formes) ---
  {
    id: 'note-style-baroque',
    title: 'Le style Baroque',
    shortDescription: 'Environ 1600–1750 : basse continue, cadences marquées, tierce picarde.',
    content: `Le **baroque** couvre environ **1600–1750**. La basse continue et l’écriture en contrepoint dominent. Les **cadences** sont très marquées ; la **tierce picarde** (conclusion en majeur dans une pièce mineure) est typique.

**Compositeurs emblématiques** : Bach, Vivaldi, Handel, Telemann, Purcell, Couperin, Scarlatti.

En analyse : repérez les cadences (demi-cadence, cadence parfaite), les mouvements obligés (sensible, septième), et les couleurs comme la tierce picarde en fin de pièce mineure.`,
    category: 'contexte',
    order: 1
  },
  {
    id: 'note-style-classique',
    title: 'Le style Classique',
    shortDescription: 'Environ 1750–1820 : phrase carrée, cadence parfaite composée, V/V.',
    content: `Le **classicisme** (environ **1750–1820**) privilégie la **phrase carrée** et la clarté harmonique. La **cadence parfaite composée** (I6/4 → V → I) est la formule type de conclusion. L’usage de la **dominante de la dominante (V/V)** devient systématique.

**Compositeurs emblématiques** : Haydn, Mozart, Beethoven (transition classique/romantique), Gluck, Clementi.

En analyse : repérez T–SD–D–T, les cadences (demi, parfaite, 6/4 cadentiel), et les enchaînements typiques.`,
    category: 'contexte',
    order: 2
  },
  {
    id: 'note-style-romantique',
    title: 'Le style Romantique',
    shortDescription: 'Environ 1820–1900 : couleurs chromatiques, N6, cadence rompue.',
    content: `Le **romantisme** (environ **1820–1900**) enrichit l’harmonie : **cadences retardées**, **sixte napolitaine (N6)**, **cadence rompue** pour l’effet de surprise, et usage étendu de **V/V** et couleurs chromatiques.

**Compositeurs emblématiques** : Schubert, Chopin, Schumann, Liszt, Brahms, Mendelssohn, Wagner, Verdi, Tchaikovsky, Dvořák.

En analyse : mêmes fonctions T–SD–D–T, avec davantage de couleurs (N6, V/V, cadence rompue) et de modulations.`,
    category: 'contexte',
    order: 3
  },
  {
    id: 'note-style-moderne',
    title: 'Le style Moderne',
    shortDescription: 'À partir d’environ 1900 : extensions tonales, puis atonalité.',
    content: `La **musique moderne** (à partir d’environ **1900**) prolonge d’abord la tonalité (Debussy, Ravel, early Stravinsky), puis explore l’**atonalité** et le **sérialisme** (Schoenberg, Berg, Webern), ou des langages personnels (Bartók, Prokofiev, Shostakovich).

En analyse harmonique tonale : les œuvres du début du XXe utilisent encore cadences, fonctions et couleurs (N6, V/V), avec des extensions et des ambiguïtés tonales.`,
    category: 'contexte',
    order: 4
  },
  {
    id: 'note-compositeurs',
    title: 'Quelques compositeurs',
    shortDescription: 'Repères sur les compositeurs les plus présents dans les exercices.',
    content: `Courtes notices sur les compositeurs que vous croisez dans les exercices : période, genres typiques, et lien avec les notions du Codex.`,
    category: 'contexte',
    order: 5,
    sections: [
      { id: 'bach', title: 'J.S. Bach (baroque)', content: `**1685–1750**. Cantates, chorals, suites, concertos, *Clavier bien tempéré*. Cadences très nettes, tierce picarde en mineur, basse continue. En exercice : repérez les cadences parfaites, demi-cadences, mouvements obligés.` },
      { id: 'haydn', title: 'Haydn (classique)', content: `**1732–1809**. Symphonies, quatuors, sonates. Phrase carrée, cadence parfaite composée (I6/4–V–I), usage de V/V. Modèle de la phrase tonale classique.` },
      { id: 'mozart', title: 'Mozart (classique)', content: `**1756–1791**. Opéras, symphonies, concertos, sonates. Clarté harmonique, cadences parfaites et demi-cadences, V/V très présent. Idéal pour repérer T–SD–D–T.` },
      { id: 'beethoven', title: 'Beethoven (classique / romantique)', content: `**1770–1827**. Sonates, symphonies, quatuors. Transition classique–romantique ; cadences marquées, puis couleurs plus dramatiques (N6, modulations).` },
      { id: 'schubert', title: 'Schubert (romantique)', content: `**1797–1828**. Lieder, symphonies, sonates. Couleurs romantiques (N6, V/V, cadence rompue), tout en restant tonal.` },
      { id: 'chopin', title: 'Chopin (romantique)', content: `**1810–1849**. Nocturnes, ballades, préludes, études. Harmonie riche (N6, V/V, cadences retardées), phrase souple.` },
      { id: 'schumann', title: 'Schumann (romantique)', content: `**1810–1856**. Lieder, pièces pour piano, symphonies. Couleurs romantiques, cadence rompue, modulations.` },
      { id: 'brahms', title: 'Brahms (romantique)', content: `**1833–1897**. Symphonies, quatuors, lieder. Tonalité maîtrisée, cadences et enchaînements typiques, parfois couleurs (N6, V/V).` },
      { id: 'debussy', title: 'Debussy (moderne)', content: `**1862–1918**. Préludes, *Pelléas*, orchestre. Extensions tonales, couleurs (modes, quintes parallèles) ; cadences encore repérables dans beaucoup d’œuvres.` }
    ]
  },
  {
    id: 'note-formes-genres',
    title: 'Formes et genres',
    shortDescription: 'Symphonie, sonate, concerto, lied… repères sur les types d’œuvres.',
    content: `Les **formes** et **genres** structurent le répertoire. En analyse, le genre (sonate, concerto, lied…) aide à situer l’extrait : exposition, développement, récapitulation en sonate ; couplets/refrain en lied, etc.`,
    category: 'contexte',
    order: 6,
    sections: [
      { id: 'symphonie', title: 'Symphonie', content: `Œuvre pour **orchestre** en plusieurs mouvements (souvent 4). Structure type : vif – lent – menuet/scherzo – vif. Cadences et fonctions T–SD–D–T s’y repèrent dans chaque mouvement.` },
      { id: 'concerto', title: 'Concerto', content: `Dialogue **soliste / orchestre**, généralement 3 mouvements. Cadences (demi-cadence, parfaite) structurent les phrases ; la « cadence » soliste (improvisation ou écrite) joue sur la dominante avant la récapitulation.` },
      { id: 'sonate', title: 'Sonate', content: `Forme en **plusieurs mouvements** (souvent 3 ou 4). Le 1er mouvement suit souvent la forme sonate : exposition – développement – récapitulation. Idéal pour repérer cadences, modulations et enchaînements.` },
      { id: 'trio', title: 'Trio', content: `Œuvre pour **trois instruments** (ex. piano, violon, violoncelle). Structure proche de la sonate ; mêmes repères harmoniques (cadences, T–SD–D–T).` },
      { id: 'quatuor_cordes', title: 'Quatuor à cordes', content: `Deux violons, alto, violoncelle. Forme en mouvements (souvent 4). Phrase et cadences très présentes ; modèle du classicisme (Haydn, Mozart, Beethoven).` },
      { id: 'lied_melodie', title: 'Lied / Mélodie', content: `**Chant et piano** sur un poème. Forme liée au texte (strophes, through-composed). Harmonie au service du texte ; cadences et couleurs (N6, V/V) fréquentes.` },
      { id: 'opera', title: 'Opéra', content: `Œuvre **scénique** avec chant et orchestre. Airs, récitatifs, ensembles. Cadences marquent les fins de phrases et les conclusions ; modulations pour les contrastes dramatiques.` },
      { id: 'prelude', title: 'Prélude', content: `Pièce courte, souvent pour **piano**. Peut être libre (Chopin, Debussy) ou structuré (Bach). Cadences et enchaînements typiques selon l’époque.` },
      { id: 'nocturne', title: 'Nocturne', content: `Pièce **lyrique** pour piano (ou orchestre). Style romantique ; couleurs (N6, V/V, cadence rompue), mélodie chantante.` },
      { id: 'ballade', title: 'Ballade', content: `Pièce **narrative** pour piano (ou voix). Forme libre, harmonie riche ; repérage des cadences et des couleurs romantiques.` }
    ]
  }
]
